import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Archive,
  ArchiveRestore,
  Pencil,
  Trash2,
} from "lucide-react";
import { gql, GraphQLError } from "../lib/api";
import {
  GET_APPLICATION_QUERY,
  GET_APPLICATION_AUDIT_LOG_QUERY,
  RESEND_SIGNATURE_MUTATION,
  ABANDON_APPLICATION_MUTATION,
  ARCHIVE_APPLICATION_MUTATION,
  UNARCHIVE_APPLICATION_MUTATION,
  DELETE_APPLICATION_MUTATION,
  RESEND_INTAKE_LINK_MUTATION,
  REVOKE_INTAKE_TOKEN_MUTATION,
  SUBMIT_FOR_BOARDING_MUTATION,
  GENERATE_MNSP_PACKET_DOWNLOAD_URL_MUTATION,
  VERIFY_MNSP_PDF_HASH_MUTATION,
} from "../lib/operations";
import { Loader } from "../components/Loader";
import { ErrorMessage } from "../components/ErrorMessage";
import { StatusBadge } from "../components/StatusBadge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EditApplicationModal } from "../components/EditApplicationModal";
import { selectPricingTier } from "../lib/pricingTiers";
import { displayBusinessName } from "../lib/businessName";
import { pdiOrderWanted } from "../lib/types";
import type {
  Application,
  ApplicationResult,
  AuditLogEntry,
  MnspSignatureMeta,
} from "../lib/types";
import { storeTypes } from "@/lib/storeTypes";

// Discriminated union for the confirm dialog state. Null when no
// dialog is open; otherwise carries the kind so the dialog renders
// the right title + body + behavior.
type DialogState =
  | null
  | { kind: "abandon" }
  | { kind: "revoke-intake" }
  | { kind: "submit-boarding" }
  | { kind: "archive" }
  | { kind: "unarchive" }
  | { kind: "delete" };

// Terminal statuses where the abandon action makes no sense.
const NON_ABANDONABLE_STATUSES = new Set(["BOARDED", "ABANDONED", "ARCHIVED"]);

// Statuses where updateApplication rejects with INVALID_STATE (see
// cardConnectStartHandler._handle_update_application -- keep this set in sync).
// Terminal (BOARDED/ABANDONED/ARCHIVED) plus signed/in-review: once a document
// is signed it is read-only and cannot be edited or have its MAR regenerated
// (an addendum is required instead -- phase 2). MNSP_SIGNED is ONLY reachable
// for an ApplicationPath=MNSP_ENROLLMENT_ONLY row (it never touches
// CardConnect, so signing the MAR here IS the whole application) -- a
// BUNDLED row's MnspOnly signature never sets Status to MNSP_SIGNED,
// specifically so it doesn't land here while its separate CardConnect
// signature/boarding is still in flight (see intakeApiHandler._handle_
// complete's mnsp_only branch). IN_REVIEW/APPROVED are post-signature on
// the bundled path. ERROR/REJECTED stay editable so the operator can fix
// and retry.
const NON_EDITABLE_STATUSES = new Set([
  "BOARDED",
  "ABANDONED",
  "ARCHIVED",
  "IN_REVIEW",
  "APPROVED",
  "MNSP_SIGNED",
]);

// Statuses where the row was dispatched via the send-form workflow
// (vs the operator-fills-it path). If any of these are present on the
// row, the intake card renders; otherwise it stays hidden.
const INTAKE_STATUSES = new Set([
  "LINK_SENT",
  "LINK_OPENED",
  "REVOKED",
  "FORM_IN_PROGRESS",
  "READY_FOR_REVIEW",
]);

// Statuses where submitForBoarding will succeed (see _SUBMIT_OK_STATUSES
// in the Lambda).
const SUBMIT_OK_STATUSES = new Set([
  "READY_FOR_REVIEW",
  "FORM_IN_PROGRESS",
  "LINK_OPENED",
  "LINK_SENT",
  "REVOKED",
]);

// Newest-first. The resolver returns audit entries unsorted because
// APPSYNC_JS doesn't allow arrow-function comparators on .sort(); doing
// it here is trivial. Shared by the initial load and every post-action
// refetch so the audit table always renders in the same order.
function sortAuditLog(logs: AuditLogEntry[]): AuditLogEntry[] {
  return [...logs].sort((a, b) =>
    a.timestamp < b.timestamp ? 1 : a.timestamp > b.timestamp ? -1 : 0,
  );
}

// True when no intake link has ever been successfully emailed for this row:
// a saved draft (no token minted) or a createAndSendIntake whose SES send
// failed (token minted, but the status never advanced past CREATED). The
// intake action is a first "Send" here, not a "Resend" -- drives its label,
// tooltip, and success message.
function intakeNeverSent(app: Application): boolean {
  return !app.intakeTokenExpiresAt || app.status === "CREATED";
}

export function ApplicationDetail() {
  const { applicationID } = useParams<{ applicationID: string }>();
  const navigate = useNavigate();
  const [application, setApplication] = useState<Application | null>(null);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Action-button state — one in-flight at a time across all actions.
  const [actionBusy, setActionBusy] = useState<
    | null
    | "resend"
    | "abandon"
    | "archive"
    | "unarchive"
    | "delete"
    | "resend-intake"
    | "revoke-intake"
    | "submit-boarding"
    | "mnsp-download"
    | "verify-hash"
  >(null);
  const [actionFeedback, setActionFeedback] = useState<null | {
    kind: "success" | "error";
    message: string;
  }>(null);
  // Discrete state for the Verify Hash badge so it can persist next to
  // the hash display independent of the global actionFeedback banner
  // (which a different button click would clear).
  const [verifyResult, setVerifyResult] = useState<null | {
    kind: "match" | "mismatch" | "error";
    stored?: string;
    recomputed?: string;
    message?: string;
  }>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [editOpen, setEditOpen] = useState(false);
  // True when a post-action refetch failed, so the on-screen row may be
  // stale even though the action succeeded. Drives a "refresh to see
  // latest" note.
  const [refetchFailed, setRefetchFailed] = useState(false);

  async function refetchApplication() {
    if (!applicationID) return;
    // The application row is about to change under us; a prior hash-verify
    // badge no longer describes what's on screen, so clear it.
    setVerifyResult(null);
    // allSettled, not all: the audit-log query is a secondary panel, not
    // the primary entity. If it alone fails, the action still succeeded
    // server-side and the application row itself may well have refreshed
    // fine -- neither should be masked by (or blocked on) the other.
    const [appResult, logResult] = await Promise.allSettled([
      gql<{ getApplication: Application | null }>(GET_APPLICATION_QUERY, {
        applicationID,
      }),
      gql<{
        getApplicationAuditLog: { logs: AuditLogEntry[]; nextToken?: string };
      }>(GET_APPLICATION_AUDIT_LOG_QUERY, { applicationID }),
    ]);

    if (appResult.status === "fulfilled") {
      if (appResult.value.getApplication) {
        setApplication(appResult.value.getApplication);
      }
      setRefetchFailed(false);
    } else {
      // The action itself succeeded, but re-reading the row did not. Don't
      // swallow it silently: the operator would see a success banner over
      // stale data. Surface a "refresh to see latest" note instead.
      setRefetchFailed(true);
    }

    if (logResult.status === "fulfilled") {
      setAuditLog(sortAuditLog(logResult.value.getApplicationAuditLog.logs));
    }
    // else: leave the previously loaded audit rows on screen. A failed
    // audit-log refetch isn't worth its own "stale" banner -- the next
    // successful action (or a manual page reload) will catch it up.
  }

  async function handleResendSignature() {
    if (!applicationID || actionBusy) return;
    setActionBusy("resend");
    setActionFeedback(null);
    try {
      const data = await gql<{ resendSignature: ApplicationResult }>(
        RESEND_SIGNATURE_MUTATION,
        { applicationID },
      );
      const r = data.resendSignature;
      if (r.status === "success") {
        setActionFeedback({
          kind: "success",
          message: "Signature link resent.",
        });
        await refetchApplication();
      } else {
        setActionFeedback({
          kind: "error",
          message: `${r.message ?? "Resend failed."}${
            r.errorCode ? ` (${r.errorCode})` : ""
          }`,
        });
      }
    } catch (err: unknown) {
      setActionFeedback({
        kind: "error",
        message: err instanceof GraphQLError ? err.message : "Resend failed.",
      });
    } finally {
      setActionBusy(null);
    }
  }

  async function performAbandon(reason: string) {
    if (!applicationID || actionBusy) return;
    setActionBusy("abandon");
    setActionFeedback(null);
    try {
      const data = await gql<{ abandonApplication: ApplicationResult }>(
        ABANDON_APPLICATION_MUTATION,
        { applicationID, reason },
      );
      const r = data.abandonApplication;
      if (r.status === "success") {
        setActionFeedback({
          kind: "success",
          message: "Application abandoned.",
        });
        setDialog(null);
        await refetchApplication();
      } else {
        setActionFeedback({
          kind: "error",
          message: `${r.message ?? "Abandon failed."}${
            r.errorCode ? ` (${r.errorCode})` : ""
          }`,
        });
        setDialog(null);
      }
    } catch (err: unknown) {
      setActionFeedback({
        kind: "error",
        message: err instanceof GraphQLError ? err.message : "Abandon failed.",
      });
      setDialog(null);
    } finally {
      setActionBusy(null);
    }
  }

  async function performArchive() {
    if (!applicationID || actionBusy || !application) return;
    setActionBusy("archive");
    setActionFeedback(null);
    try {
      await gql<{ updateApplicationStatus: Application }>(
        ARCHIVE_APPLICATION_MUTATION,
        { applicationID, preArchiveStatus: application.status },
      );
      setActionFeedback({ kind: "success", message: "Application archived." });
      setDialog(null);
      await refetchApplication();
    } catch (err: unknown) {
      setActionFeedback({
        kind: "error",
        message: err instanceof GraphQLError ? err.message : "Archive failed.",
      });
      setDialog(null);
    } finally {
      setActionBusy(null);
    }
  }

  async function performUnarchive() {
    if (!applicationID || actionBusy || !application?.preArchiveStatus) return;
    setActionBusy("unarchive");
    setActionFeedback(null);
    try {
      await gql<{ updateApplicationStatus: Application }>(
        UNARCHIVE_APPLICATION_MUTATION,
        { applicationID, status: application.preArchiveStatus },
      );
      setActionFeedback({
        kind: "success",
        message: "Application unarchived.",
      });
      setDialog(null);
      await refetchApplication();
    } catch (err: unknown) {
      setActionFeedback({
        kind: "error",
        message:
          err instanceof GraphQLError ? err.message : "Unarchive failed.",
      });
      setDialog(null);
    } finally {
      setActionBusy(null);
    }
  }

  async function performDelete() {
    if (!applicationID || actionBusy) return;
    setActionBusy("delete");
    setActionFeedback(null);
    try {
      const data = await gql<{ deleteApplication: ApplicationResult }>(
        DELETE_APPLICATION_MUTATION,
        { applicationID },
      );
      const r = data.deleteApplication;
      if (r.status === "success") {
        // The row is gone — nothing left here to refetch or show a
        // success banner over. Send the operator back to the list.
        navigate("/applications");
        return;
      }
      setActionFeedback({
        kind: "error",
        message: `${r.message ?? "Delete failed."}${
          r.errorCode ? ` (${r.errorCode})` : ""
        }`,
      });
      setDialog(null);
    } catch (err: unknown) {
      setActionFeedback({
        kind: "error",
        message: err instanceof GraphQLError ? err.message : "Delete failed.",
      });
      setDialog(null);
    } finally {
      setActionBusy(null);
    }
  }

  // ---- Send-form intake action handlers ----------------------------------

  async function handleResendIntake() {
    if (!applicationID || actionBusy) return;
    // Captured before the request so the message matches what the operator
    // clicked (a successful send flips the row to LINK_SENT).
    const firstSend = !!application && intakeNeverSent(application);
    setActionBusy("resend-intake");
    setActionFeedback(null);
    try {
      const data = await gql<{ resendIntakeLink: ApplicationResult }>(
        RESEND_INTAKE_LINK_MUTATION,
        { applicationID },
      );
      const r = data.resendIntakeLink;
      if (r.status === "success") {
        setActionFeedback({
          kind: "success",
          message: firstSend
            ? "Intake link sent to the recipient."
            : "Intake link rotated and re-sent.",
        });
        await refetchApplication();
      } else {
        setActionFeedback({
          kind: "error",
          message: `${r.message ?? (firstSend ? "Send failed." : "Resend failed.")}${
            r.errorCode ? ` (${r.errorCode})` : ""
          }`,
        });
      }
    } catch (err: unknown) {
      setActionFeedback({
        kind: "error",
        message:
          err instanceof GraphQLError
            ? err.message
            : firstSend
              ? "Send failed."
              : "Resend failed.",
      });
    } finally {
      setActionBusy(null);
    }
  }

  async function performRevokeIntake() {
    if (!applicationID || actionBusy) return;
    setActionBusy("revoke-intake");
    setActionFeedback(null);
    try {
      const data = await gql<{ revokeIntakeToken: ApplicationResult }>(
        REVOKE_INTAKE_TOKEN_MUTATION,
        { applicationID },
      );
      const r = data.revokeIntakeToken;
      if (r.status === "success") {
        setActionFeedback({ kind: "success", message: "Intake link revoked." });
        setDialog(null);
        await refetchApplication();
      } else {
        setActionFeedback({
          kind: "error",
          message: `${r.message ?? "Revoke failed."}${
            r.errorCode ? ` (${r.errorCode})` : ""
          }`,
        });
        setDialog(null);
      }
    } catch (err: unknown) {
      setActionFeedback({
        kind: "error",
        message: err instanceof GraphQLError ? err.message : "Revoke failed.",
      });
      setDialog(null);
    } finally {
      setActionBusy(null);
    }
  }

  async function handleDownloadMnspPacket() {
    if (!applicationID || actionBusy) return;
    setActionBusy("mnsp-download");
    setActionFeedback(null);
    try {
      const data = await gql<{
        generateMnspPacketDownloadUrl: ApplicationResult;
      }>(GENERATE_MNSP_PACKET_DOWNLOAD_URL_MUTATION, { applicationID });
      const r = data.generateMnspPacketDownloadUrl;
      // The presigned URL is tunnelled through `message` on success;
      // open in a new tab so the operator's dashboard view is preserved
      // and the URL never lands in the address bar (where it'd survive
      // in history past its 5-minute TTL).
      if (
        r.status === "success" &&
        r.message &&
        /^https:\/\//i.test(r.message)
      ) {
        // Guard the scheme before handing the string to window.open: it's
        // a presigned S3 URL and must be https. Refusing anything else
        // stops a malformed/ tampered `message` from becoming a
        // javascript:/data: navigation.
        window.open(r.message, "_blank", "noopener,noreferrer");
      } else if (r.status === "success") {
        setActionFeedback({
          kind: "error",
          message: "Download URL was malformed. Please try again.",
        });
      } else {
        setActionFeedback({
          kind: "error",
          message: `${r.message ?? "Could not generate download URL."}${
            r.errorCode ? ` (${r.errorCode})` : ""
          }`,
        });
      }
    } catch (err: unknown) {
      setActionFeedback({
        kind: "error",
        message:
          err instanceof GraphQLError
            ? err.message
            : "Could not generate download URL.",
      });
    } finally {
      setActionBusy(null);
    }
  }

  async function handleVerifyMnspPdfHash() {
    if (!applicationID || actionBusy) return;
    setActionBusy("verify-hash");
    setVerifyResult(null);
    try {
      const data = await gql<{ verifyMnspPdfHash: ApplicationResult }>(
        VERIFY_MNSP_PDF_HASH_MUTATION,
        { applicationID },
      );
      const r = data.verifyMnspPdfHash;
      if (r.status === "success" && r.message === "match") {
        setVerifyResult({ kind: "match" });
      } else if (r.status === "success" && r.message?.startsWith("mismatch:")) {
        // Backend returns "mismatch:<stored>:<recomputed>" so the
        // operator (or the next engineer) can read both halves
        // without a second round-trip.
        const [, stored, recomputed] = r.message.split(":");
        setVerifyResult({ kind: "mismatch", stored, recomputed });
      } else if (r.status === "success") {
        // Succeeded, but the message is neither "match" nor "mismatch:...".
        // Don't collapse this into a generic failure: it's an unexpected
        // response, not a hash mismatch.
        setVerifyResult({
          kind: "error",
          message: `Verification returned an unexpected response${
            r.message ? `: ${r.message}` : ""
          }.`,
        });
      } else {
        setVerifyResult({
          kind: "error",
          message: `${r.message ?? "Verification failed."}${
            r.errorCode ? ` (${r.errorCode})` : ""
          }`,
        });
      }
    } catch (err: unknown) {
      setVerifyResult({
        kind: "error",
        message:
          err instanceof GraphQLError ? err.message : "Verification failed.",
      });
    } finally {
      setActionBusy(null);
    }
  }

  async function performSubmitForBoarding() {
    if (!applicationID || actionBusy) return;
    setActionBusy("submit-boarding");
    setActionFeedback(null);
    try {
      const data = await gql<{ submitForBoarding: ApplicationResult }>(
        SUBMIT_FOR_BOARDING_MUTATION,
        { applicationID },
      );
      const r = data.submitForBoarding;
      if (r.status === "success") {
        setActionFeedback({
          kind: "success",
          message: "Submitted. Merchant will receive the signature email.",
        });
        setDialog(null);
        await refetchApplication();
      } else {
        setActionFeedback({
          kind: "error",
          message: `${r.message ?? "Submit failed."}${
            r.errorCode ? ` (${r.errorCode})` : ""
          }`,
        });
        setDialog(null);
      }
    } catch (err: unknown) {
      setActionFeedback({
        kind: "error",
        message: err instanceof GraphQLError ? err.message : "Submit failed.",
      });
      setDialog(null);
    } finally {
      setActionBusy(null);
    }
  }

  useEffect(() => {
    if (!applicationID) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Clear per-application transient state so a stale hash-verify badge or
    // refetch note from a previously viewed application doesn't carry over.
    setVerifyResult(null);
    setRefetchFailed(false);

    Promise.all([
      gql<{ getApplication: Application | null }>(GET_APPLICATION_QUERY, {
        applicationID,
      }),
      gql<{
        getApplicationAuditLog: { logs: AuditLogEntry[]; nextToken?: string };
      }>(GET_APPLICATION_AUDIT_LOG_QUERY, { applicationID }),
    ])
      .then(([appData, logData]) => {
        if (cancelled) return;
        setApplication(appData.getApplication);
        setAuditLog(sortAuditLog(logData.getApplicationAuditLog.logs));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof GraphQLError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed to load application.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applicationID]);

  if (loading) {
    return (
      <div className="py-8">
        <Loader label="Loading application..." />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!application) {
    return (
      <div className="card p-8 text-center">
        <p className="text-brand-muted">Application not found.</p>
        <Link
          to="/applications"
          className="btn-secondary mt-6 inline-block py-2"
        >
          Back to list
        </Link>
      </div>
    );
  }

  // Whether a PDI managed-network order is in play for this application --
  // see pdiOrderWanted. When it's false, the PDI cards below are hidden
  // entirely.
  const pdiOrderInPlay = pdiOrderWanted(
    application.pdiPrefs?.sendPdiOrder,
    application.applicationPath === "MNSP_ENROLLMENT_ONLY",
  );

  return (
    <div>
      <Link
        to="/applications"
        className="mb-4 inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-text"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to applications
      </Link>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-brand-text">
            {displayBusinessName(application)}
          </h1>
          {/* Keep the legal name visible when it differs from the DBA the
              header now leads with -- operators still need it for the
              CardConnect paperwork. */}
          {application.dbaName?.trim() &&
            application.dbaName.trim() !== application.businessName?.trim() && (
              <p className="mt-1 text-sm text-brand-muted">
                Legal: {application.businessName}
              </p>
            )}
          <p className="mt-1 text-sm text-brand-muted">
            {application.merchantEmail}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={application.status} />
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            disabled={NON_EDITABLE_STATUSES.has(application.status)}
            className="rounded p-1.5 text-brand-muted hover:bg-brand-bg hover:text-brand-text disabled:cursor-not-allowed disabled:opacity-40"
            title={
              NON_EDITABLE_STATUSES.has(application.status)
                ? `Cannot edit a ${application.status} application`
                : "Edit application"
            }
            aria-label="Edit application"
          >
            <Pencil className="h-4 w-4" />
          </button>
          {application.status === "ARCHIVED" ? (
            <>
              <button
                type="button"
                onClick={() => setDialog({ kind: "unarchive" })}
                disabled={actionBusy !== null || !application.preArchiveStatus}
                className="rounded p-1.5 text-brand-muted hover:bg-brand-bg hover:text-brand-text disabled:cursor-not-allowed disabled:opacity-40"
                title={
                  application.preArchiveStatus
                    ? `Restore to ${application.preArchiveStatus}`
                    : "Original status unknown — can't unarchive"
                }
                aria-label="Unarchive application"
              >
                <ArchiveRestore className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setDialog({ kind: "delete" })}
                disabled={actionBusy !== null}
                className="rounded p-1.5 text-danger hover:bg-brand-bg hover:text-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                title="Permanently delete application"
                aria-label="Delete application"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setDialog({ kind: "archive" })}
              disabled={actionBusy !== null}
              className="rounded p-1.5 text-brand-muted hover:bg-brand-bg hover:text-brand-text disabled:cursor-not-allowed disabled:opacity-40"
              title="Archive application"
              aria-label="Archive application"
            >
              <Archive className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <EditApplicationModal
        open={editOpen}
        application={application}
        onClose={() => setEditOpen(false)}
        onSaved={(message, kind = "success") => {
          setEditOpen(false);
          setActionFeedback({
            // "warning" = the row saved but a best-effort CardConnect step
            // failed (e.g. the signature link) -- show it red, not green.
            kind: kind === "warning" ? "error" : "success",
            message: message ?? "Application updated.",
          });
          void refetchApplication();
        }}
      />

      {actionFeedback && (
        <p
          role={actionFeedback.kind === "error" ? "alert" : "status"}
          aria-live="polite"
          className={
            actionFeedback.kind === "success"
              ? "sticky top-0 z-20 mb-3 rounded-lg border border-success/30 bg-success/10 px-3.5 py-2.5 text-sm text-emerald-200 shadow-card"
              : "callout-danger sticky top-0 z-20 mb-3 px-3.5 py-2.5 shadow-card"
          }
        >
          {actionFeedback.message}
        </p>
      )}

      {/* Operator-facing "not submitted yet" banner. The applications list
          shows a status badge per row, but on the drill-in an operator asked
          for something unmissable that says the application has not been
          submitted. Mirrors the merchant-facing clarity added in #156. Two
          sub-states: the applicant has not submitted their intake at all
          (LINK_SENT / LINK_OPENED / FORM_IN_PROGRESS), vs the applicant
          submitted and it is now waiting for the operator to submit it for
          boarding (READY_FOR_REVIEW). Gated on the link actually having gone
          out — a draft the operator prepared (or a send that failed) gets the
          red "no link sent yet" banner below instead, whose wording fits. */}
      {(application.status === "LINK_SENT" ||
        application.status === "LINK_OPENED" ||
        application.status === "FORM_IN_PROGRESS" ||
        application.status === "READY_FOR_REVIEW") &&
        !intakeNeverSent(application) && (
          <p
            role="status"
            aria-live="polite"
            className="mb-4 rounded-lg border border-warning/30 bg-warning/10 px-3.5 py-2.5 text-sm text-amber-200"
          >
            {application.status === "READY_FOR_REVIEW"
              ? "Not submitted for boarding yet. The applicant has completed and submitted their intake; submit it for boarding when you are ready."
              : `Not submitted yet. The applicant has not submitted this application (currently ${application.intakeProgressPct ?? 0}% complete).`}
          </p>
        )}

      {/* Loud, red "no link sent" banner — a staged / never-dispatched intake
          row (a draft with a recipient, a complete draft sitting at
          READY_FOR_REVIEW, or a send that failed) where no link has actually
          reached the recipient. Pulled up here from the Intake form card so
          an operator can't miss that nothing has been emailed. When the row
          is already complete (READY_FOR_REVIEW) it also names the
          Submit-to-CardConnect option, since both actions are available. */}
      {(INTAKE_STATUSES.has(application.status) ||
        application.intakeRecipientEmail) &&
        intakeNeverSent(application) && (
          <p
            role="status"
            aria-live="polite"
            className="callout-danger mb-4 px-3.5 py-2.5"
          >
            {application.status === "READY_FOR_REVIEW" ? (
              <>
                This application is complete, but{" "}
                <span className="font-semibold">
                  no intake link has been sent
                </span>
                . Submit it to CardConnect below, or use{" "}
                <span className="font-semibold">Send intake link</span> to have
                the recipient review and finish it.
              </>
            ) : (
              <>
                No intake link has been sent yet. Use{" "}
                <span className="font-semibold">Send intake link</span> below to
                email the recipient a link to finish this application.
              </>
            )}
          </p>
        )}

      <div className="grid gap-6 md:grid-cols-2">
        <DetailCard title="Application">
          <DetailRow label="Application ID" value={application.applicationID} />
          <DetailRow label="Template" value={application.templateID ?? "—"} />
          <DetailRow
            label="Sales code"
            value={application.salesCode ?? "Auto (by volume)"}
          />
          <DetailRow
            label="CardConnect merchant ID"
            value={application.cardConnectMerchantID ?? "—"}
          />
          <DetailRow
            label="Front-end MID"
            value={application.frontEndMid ?? "—"}
          />
          <DetailRow
            label="MCC Sub Category"
            value={
              storeTypes?.find((t) => t.value === application.storeType)
                ?.name ?? "—"
            }
          />
          <DetailRow
            label="Assigned salesperson PIN"
            value={application.assignedToPIN ?? "—"}
          />
          <DetailRow
            label="Pricing tier"
            value={(() => {
              const tier = selectPricingTier(application.averageMonthlyVolume);
              if (!tier) return "—";
              const placeholder = tier.isPlaceholder ? " (placeholder)" : "";
              return `${tier.label}${placeholder}`;
            })()}
          />
          <DetailRow
            label="Created"
            value={
              application.createdAt
                ? new Date(application.createdAt).toLocaleString()
                : "—"
            }
          />
          <DetailRow
            label="Updated"
            value={
              application.updatedAt
                ? new Date(application.updatedAt).toLocaleString()
                : "—"
            }
          />
        </DetailCard>

        <DetailCard title="Polling and errors">
          <DetailRow
            label="Poll attempts"
            value={
              application.pollAttempts != null
                ? String(application.pollAttempts)
                : "—"
            }
          />
          <DetailRow
            label="Next poll after"
            value={
              application.nextPollAfter
                ? new Date(application.nextPollAfter).toLocaleString()
                : "—"
            }
          />
          <DetailRow
            label="Error details"
            value={application.errorDetails ?? "—"}
          />
          <DetailRow
            label="Signature URL"
            value={
              application.signatureUrl ? (
                <a
                  href={application.signatureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-blue underline-offset-2 hover:underline"
                >
                  Open signature link
                </a>
              ) : (
                "—"
              )
            }
          />
        </DetailCard>

        {pdiOrderInPlay && (
          <>
            <DetailCard title="PDI managed network order">
              <DetailRow
                label="Order status"
                value={
                  // New rows carry the DRY_RUN status; legacy rows carry SENT +
                  // the pdiOrderDryRun flag. Show both as a clear dry-run label so
                  // a suppressed order is never read as a live PDI send.
                  application.pdiOrderStatus === "DRY_RUN" ||
                  (application.pdiOrderStatus === "SENT" &&
                    application.pdiOrderDryRun)
                    ? "Dry run (not sent to PDI)"
                    : (application.pdiOrderStatus ?? "NOT_SENT")
                }
              />
              <DetailRow
                label="Sent at"
                value={
                  application.pdiOrderSentAt
                    ? new Date(application.pdiOrderSentAt).toLocaleString()
                    : "—"
                }
              />
              <DetailRow label="Error" value={application.pdiOrderError ?? "—"} />
            </DetailCard>

            {/* Operator-collected order preferences. Lifted into its own
                DetailCard for visual parity with the rest of the grid —
                previously this lived inside the PDI order card and made
                that card much taller than the others. The block always
                renders (so PDI fields are visible even for rows pre-dating
                the schema addition — empty values just show as "—"). */}
            <DetailCard title="PDI order preferences">
              {/* This whole card only renders when an order is in play; a "No"
                  selection hides it. So this row just notes explicit vs default. */}
              <DetailRow
                label="Send PDI order"
                value={
                  application.pdiPrefs?.sendPdiOrder === "Yes"
                    ? "Yes"
                    : "Yes (default)"
                }
              />
              <DetailRow
                label="POS platform"
                value={application.pdiPrefs?.posPlatform || "—"}
              />
              <DetailRow
                label="Sites"
                value={application.pdiPrefs?.singleOrMultiSite || "Single"}
              />
              <DetailRow
                label="Gas pumps"
                value={
                  application.pdiPrefs?.gasPumpYn === "Yes"
                    ? `Yes — ${
                        application.pdiPrefs.gasPumpType || "type not specified"
                      }`
                    : application.pdiPrefs?.gasPumpYn || "No"
                }
              />
              <DetailRow
                label="New broadband"
                value={application.pdiPrefs?.broadbandYn || "No"}
              />
              <DetailRow
                label="Residential"
                value={application.pdiPrefs?.residentialLocation || "No"}
              />
              <DetailRow
                label="Managed WiFi"
                value={application.pdiPrefs?.managedWifiInterest || "—"}
              />
              <DetailRow
                label="Shipping"
                value={application.pdiPrefs?.shippingSpeed || "Standard"}
              />
              {application.pdiPrefs?.notes && (
                <DetailRow
                  label="Notes"
                  value={
                    <span className="whitespace-pre-wrap">
                      {application.pdiPrefs.notes}
                    </span>
                  }
                />
              )}
            </DetailCard>
          </>
        )}

        {/* Send-form intake card — only rendered when this row was
            dispatched via the send-form workflow (or is in any intake
            state, including post-revoke for visibility). Titled to make it
            obvious when this is a staged draft whose link hasn't gone out
            yet, vs a live intake the recipient is working through. */}
        {(INTAKE_STATUSES.has(application.status) ||
          application.intakeRecipientEmail) && (
          <div className="col-span-full">
            <DetailCard
              title={
                intakeNeverSent(application)
                  ? "Intake link (not sent yet)"
                  : "Intake form"
              }
            >
              <DetailRow
                label="Recipient"
                value={
                  application.intakeRecipientEmail
                    ? `${application.intakeRecipientType ?? "?"} — ${
                        application.intakeRecipientEmail
                      }${
                        application.intakeCcEmail
                          ? `, ${application.intakeCcEmail}`
                          : ""
                      }`
                    : "—"
                }
              />
              <DetailRow
                label="Link expires"
                value={
                  application.intakeTokenExpiresAt
                    ? new Date(
                        application.intakeTokenExpiresAt,
                      ).toLocaleString()
                    : "—"
                }
              />
              <DetailRow
                label="Opens"
                value={
                  application.intakeOpensCount != null
                    ? String(application.intakeOpensCount)
                    : "0"
                }
              />
              <DetailRow
                label="First opened"
                value={
                  application.intakeFirstOpenedAt
                    ? new Date(application.intakeFirstOpenedAt).toLocaleString()
                    : "—"
                }
              />
              <DetailRow
                label="Last opened"
                value={
                  application.intakeLastOpenedAt
                    ? new Date(application.intakeLastOpenedAt).toLocaleString()
                    : "—"
                }
              />
              <DetailRow
                label="Submitted"
                value={
                  application.intakeSubmittedAt
                    ? new Date(application.intakeSubmittedAt).toLocaleString()
                    : "—"
                }
              />
              <DetailRow
                label="Revoked"
                value={application.intakeRevoked ? "Yes" : "No"}
              />
              <DetailRow
                label="Auto-submit"
                value={application.autoSubmit ? "Yes" : "No"}
              />
              <DetailRow
                label="MNSP enrollment"
                value={
                  !application.mnspEnrollmentRequested
                    ? "No"
                    : application.mnspEnrollment?.serviceType === "OTHER_MNSP"
                      ? `Yes, Other: ${
                          application.mnspEnrollment.registerOther?.trim() ||
                          "register name pending"
                        } (pricing confirmed by rep)`
                      : application.mnspEnrollment?.serviceType
                        ? `Yes, ${application.mnspEnrollment.serviceType.replace(
                            "_MNSP",
                            "",
                          )}`
                        : "Yes, service type pending"
                }
              />
              {/* Independent of MNSP enrollment above -- either, both, or
                  neither may be requested. */}
              <DetailRow
                label="Terminal rental"
                value={
                  application.terminalRentalRequested
                    ? application.mnspEnrollment?.terminalMonthlyRentalFee
                      ? `Yes, $${application.mnspEnrollment.terminalMonthlyRentalFee}/mo`
                      : "Yes, fee pending"
                    : "No"
                }
              />

              {/* Progress bar — drives off the server-calculated %.
                Updates live via the AppSync subscription when the
                recipient saves. */}
              <div className="mt-3 border-t border-brand-border pt-3">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-brand-muted">
                    Form progress
                  </span>
                  <span className="text-xs text-brand-text">
                    {application.intakeProgressPct ?? 0}%
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-brand-border">
                  <div
                    className="h-full bg-brand-blue transition-all"
                    style={{
                      width: `${Math.max(
                        2,
                        application.intakeProgressPct ?? 0,
                      )}%`,
                    }}
                  />
                </div>
              </div>
            </DetailCard>
          </div>
        )}

        {(application.mnspEnrollmentRequested ||
          application.terminalRentalRequested) && (
          <div className="col-span-full">
            <DetailCard
              title={
                application.mnspEnrollmentRequested &&
                application.terminalRentalRequested
                  ? "MNSP & rental packet"
                  : application.terminalRentalRequested
                    ? "Terminal rental packet"
                    : "MNSP packet"
              }
            >
              {application.mnspEnrollmentPdfKey ? (
                <div className="mt-2 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleDownloadMnspPacket}
                    disabled={!!actionBusy}
                    className="btn-secondary py-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {actionBusy === "mnsp-download"
                      ? "Generating link…"
                      : "Download MAR + Enrollment PDF"}
                  </button>
                  {application.mnspEnrollmentSubmittedAt && (
                    <span className="text-xs text-brand-muted">
                      Submitted{" "}
                      {new Date(
                        application.mnspEnrollmentSubmittedAt,
                      ).toLocaleString()}
                    </span>
                  )}
                </div>
              ) : (
                <p className="mt-1 text-xs text-brand-muted">
                  The PDF is rendered automatically once the merchant submits
                  the intake form. It will appear here when ready.
                </p>
              )}
              {application.mnspEnrollment?.signatureImage &&
                /^data:image\/(png|jpeg);base64,/i.test(
                  application.mnspEnrollment.signatureImage,
                ) && (
                  <div className="mt-3">
                    <p className="mb-1 text-xs uppercase tracking-wide text-brand-muted">
                      Merchant signature
                    </p>
                    {/* Allowlist the data-URL prefix before rendering as an
                        <img src>: the value is stored server-side but we
                        never want a non-image data URL (or an external URL)
                        reaching the DOM. Only base64 PNG/JPEG data URLs pass. */}
                    <img
                      src={application.mnspEnrollment.signatureImage}
                      alt="Captured merchant signature"
                      className="rounded border border-brand-border bg-white"
                      style={{ maxWidth: 400, height: "auto" }}
                    />
                  </div>
                )}
              {/* Forensic signature audit panel. Renders even when
                      only partial metadata is present so an older row
                      (missing pdfSha256 because the render predated
                      the field) still surfaces the IP/UA we did capture.
                      Hidden entirely if neither signatureMeta nor
                      pdfSha256 is populated. */}
              {(application.mnspEnrollment?.signatureMeta ||
                application.mnspEnrollment?.pdfSha256) && (
                <SignatureAuditPanel
                  meta={application.mnspEnrollment.signatureMeta ?? null}
                  pdfSha256={application.mnspEnrollment.pdfSha256 ?? null}
                  onVerify={handleVerifyMnspPdfHash}
                  verifying={actionBusy === "verify-hash"}
                  verifyResult={verifyResult}
                />
              )}
            </DetailCard>
          </div>
        )}

        {/* Operator-only notes (issue #130) -- edited via EditApplicationModal,
            never surfaced to the merchant (absent from the intake API, the
            intake form, and the CardConnect payload). Only rendered when
            there's something to show, same as the other optional cards above. */}
        {application.internalNotes && (
          <div className="col-span-full">
            <DetailCard title="Internal notes">
              <p className="whitespace-pre-wrap text-sm text-brand-text">
                {application.internalNotes}
              </p>
            </DetailCard>
          </div>
        )}

        <div className="col-span-full">
          <DetailCard title="Actions">
            {refetchFailed && (
              <p
                role="status"
                aria-live="polite"
                className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-300"
              >
                The action completed, but we couldn't refresh this view. Reload
                the page to see the latest state.
              </p>
            )}
            <div className="space-y-3">
              {/* Primary action row — boards the merchant. Only shows
                  for intake-workflow rows in READY_FOR_REVIEW. Never
                  shown for MNSP_ENROLLMENT_ONLY applications — those are
                  never submitted to CardConnect (the backend rejects the
                  mutation outright; see _handle_submit_for_boarding's
                  ApplicationPath guard). */}
              {(INTAKE_STATUSES.has(application.status) ||
                application.intakeRecipientEmail) &&
                application.applicationPath !== "MNSP_ENROLLMENT_ONLY" && (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => setDialog({ kind: "submit-boarding" })}
                      disabled={
                        actionBusy !== null ||
                        application.status !== "READY_FOR_REVIEW"
                      }
                      className="btn-primary py-2 disabled:cursor-not-allowed disabled:opacity-50"
                      title={
                        SUBMIT_OK_STATUSES.has(application.status)
                          ? "Submit the collected merchant data to CardConnect"
                          : `Cannot submit a ${application.status} application`
                      }
                    >
                      {actionBusy === "submit-boarding"
                        ? "Submitting…"
                        : "Submit to CardConnect"}
                    </button>
                  </div>
                )}

              {/* Secondary actions — resend signature, resend intake. */}
              <div className="grid grid-cols-1 gap-2 border-t border-brand-border pt-3 sm:grid-cols-2">
                {(INTAKE_STATUSES.has(application.status) ||
                  application.intakeRecipientEmail) && (
                  <button
                    type="button"
                    onClick={handleResendIntake}
                    disabled={
                      actionBusy !== null ||
                      // A never-sent draft (CREATED, no token yet) can have
                      // its first link sent from here; otherwise the same
                      // submit-window gate as before.
                      !(
                        SUBMIT_OK_STATUSES.has(application.status) ||
                        application.status === "CREATED"
                      ) ||
                      application.status === "ABANDONED" ||
                      application.status === "BOARDED" ||
                      application.status === "REJECTED"
                    }
                    className="btn-secondary py-2 disabled:cursor-not-allowed disabled:opacity-50"
                    title={
                      intakeNeverSent(application)
                        ? "Email the recipient an intake link to finish this application"
                        : "Rotate the token and re-email the same recipient"
                    }
                  >
                    {actionBusy === "resend-intake"
                      ? intakeNeverSent(application)
                        ? "Sending…"
                        : "Resending…"
                      : intakeNeverSent(application)
                        ? "Send intake link"
                        : "Resend intake link"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleResendSignature}
                  disabled={
                    actionBusy !== null ||
                    !application.cardConnectMerchantID ||
                    application.status === "SUBMITTED" ||
                    application.status === "REJECTED" ||
                    application.status === "IN_REVIEW" ||
                    application.status === "ABANDONED" ||
                    application.status === "BOARDED" ||
                    application.status === "ARCHIVED"
                  }
                  className="btn-secondary py-2 disabled:cursor-not-allowed disabled:opacity-50"
                  title={
                    !application.cardConnectMerchantID
                      ? "No CardConnect merchant ID — application never reached CardConnect"
                      : "Regenerate and re-email the digital application link"
                  }
                >
                  {actionBusy === "resend"
                    ? "Resending..."
                    : "Resend signature email"}
                </button>
              </div>

              {/* Destructive actions — abandon, revoke intake. */}
              <div className="grid grid-cols-1 gap-2 border-t border-brand-border pt-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setDialog({ kind: "abandon" })}
                  disabled={
                    actionBusy !== null ||
                    NON_ABANDONABLE_STATUSES.has(application.status)
                  }
                  className="btn-secondary py-2 disabled:cursor-not-allowed disabled:opacity-50"
                  title={
                    NON_ABANDONABLE_STATUSES.has(application.status)
                      ? `Cannot abandon a ${application.status} application`
                      : "Manually transition this application to ABANDONED"
                  }
                >
                  {actionBusy === "abandon"
                    ? "Abandoning..."
                    : "Mark abandoned"}
                </button>
                {(INTAKE_STATUSES.has(application.status) ||
                  application.intakeRecipientEmail) && (
                  <button
                    type="button"
                    onClick={() => setDialog({ kind: "revoke-intake" })}
                    disabled={
                      actionBusy !== null ||
                      !SUBMIT_OK_STATUSES.has(application.status) ||
                      !!application.intakeRevoked ||
                      application.status === "ABANDONED" ||
                      application.status === "BOARDED"
                    }
                    className="btn-secondary py-2 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Invalidate the intake link immediately"
                  >
                    {actionBusy === "revoke-intake"
                      ? "Revoking…"
                      : "Revoke intake link"}
                  </button>
                )}
              </div>
            </div>
          </DetailCard>
        </div>
      </div>

      {/* Confirmation dialogs replace the previous window.confirm/prompt
          calls. They render in the DOM root via the native <dialog>
          element so focus trap + ESC handling are free. */}
      <ConfirmDialog
        open={dialog?.kind === "abandon"}
        title="Abandon this application?"
        body={
          <>
            This marks the application as <strong>ABANDONED</strong>. The
            recipient won't be able to submit, and the audit log will record
            your reason.
          </>
        }
        reason={{
          label: "Reason",
          placeholder:
            "e.g., Duplicate of #12345; merchant decided to stay with current processor",
          minLength: 3,
          maxLength: 500,
        }}
        confirmLabel="Abandon application"
        destructive
        busy={actionBusy === "abandon"}
        onCancel={() => actionBusy !== "abandon" && setDialog(null)}
        onConfirm={(reason) => performAbandon(reason)}
      />
      <ConfirmDialog
        open={dialog?.kind === "revoke-intake"}
        title="Revoke the intake link?"
        body="The recipient won't be able to open the link again. You can resend a new intake link afterward to give them a fresh token."
        confirmLabel="Revoke link"
        destructive
        busy={actionBusy === "revoke-intake"}
        onCancel={() => actionBusy !== "revoke-intake" && setDialog(null)}
        onConfirm={performRevokeIntake}
      />
      <ConfirmDialog
        open={dialog?.kind === "submit-boarding"}
        title="Submit to CardConnect now?"
        body={
          <>
            This boards <strong>{displayBusinessName(application)}</strong> with
            CardConnect. The merchant will receive a signature email, and the
            application becomes read-only for further intake edits.
          </>
        }
        confirmLabel="Submit to CardConnect"
        busy={actionBusy === "submit-boarding"}
        onCancel={() => actionBusy !== "submit-boarding" && setDialog(null)}
        onConfirm={performSubmitForBoarding}
      />
      <ConfirmDialog
        open={dialog?.kind === "archive"}
        title="Archive this application?"
        body={
          <>
            This marks the application as <strong>ARCHIVED</strong>. It's a
            direct status change only — no other fields are affected.
          </>
        }
        confirmLabel="Archive application"
        destructive
        busy={actionBusy === "archive"}
        onCancel={() => actionBusy !== "archive" && setDialog(null)}
        onConfirm={performArchive}
      />
      <ConfirmDialog
        open={dialog?.kind === "unarchive"}
        title="Unarchive this application?"
        body={
          <>
            This restores the application to its status before archiving —
            <strong> {application.preArchiveStatus ?? "unknown"}</strong>.
          </>
        }
        confirmLabel="Unarchive application"
        busy={actionBusy === "unarchive"}
        onCancel={() => actionBusy !== "unarchive" && setDialog(null)}
        onConfirm={performUnarchive}
      />
      <ConfirmDialog
        open={dialog?.kind === "delete"}
        title="Permanently delete this application?"
        body={
          <>
            This removes <strong>{displayBusinessName(application)}</strong> and all of
            its data from Payment Products. This cannot be undone.
          </>
        }
        confirmLabel="Delete application"
        destructive
        busy={actionBusy === "delete"}
        onCancel={() => actionBusy !== "delete" && setDialog(null)}
        onConfirm={performDelete}
      />

      <div className="mt-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-text">
          Audit log
        </h2>
        {auditLog.length === 0 ? (
          <div className="card p-4 text-sm text-brand-muted">
            No audit events yet.
          </div>
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-brand-border bg-brand-bg text-xs uppercase tracking-wide text-brand-muted">
                <tr>
                  <th className="px-4 py-2">Time</th>
                  <th className="px-4 py-2">Event</th>
                  <th className="px-4 py-2">Actor</th>
                  <th className="px-4 py-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.map((e) => (
                  <tr
                    key={e.eventID}
                    className="border-b border-brand-border last:border-0"
                  >
                    <td className="whitespace-nowrap px-4 py-2 text-brand-muted">
                      {new Date(e.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 font-medium text-brand-text">
                      {e.eventType}
                    </td>
                    <td className="px-4 py-2 text-brand-muted">
                      {e.actor ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-brand-muted">
                      <AuditDetail detail={e.detail} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card p-5">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-muted">
        {title}
      </h3>
      <dl className="space-y-2 text-sm">{children}</dl>
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <dt className="text-brand-muted">{label}</dt>
      <dd className="text-right text-brand-text">{value}</dd>
    </div>
  );
}

// Turns an audit detail key into a readable label: snake_case and
// camelCase both become spaced, sentence-cased text (merchantEmail ->
// "Merchant Email", status_code -> "Status code", fromStatus -> "From
// Status"). Acronym runs are left intact so templateID -> "Template ID".
function humanizeAuditKey(key: string): string {
  const spaced = key
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Renders an audit detail value as plain text. Primitives print directly;
// booleans read Yes/No; nested objects/arrays fall back to compact JSON so
// nothing is lost, while staying on one readable line.
function formatAuditValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return JSON.stringify(value);
}

// Audit log detail cell renderer. Event details are logged as small objects
// whose keys vary per event type (status transitions, error codes, recipient
// changes, and so on). Instead of special-casing individual keys, we render
// any object as a humanized key/value list so every event reads as plain
// text rather than a raw object. A "Show raw" toggle keeps the exact JSON
// available for debugging, and new detail shapes stay readable with no code
// change here.
function AuditDetail({ detail }: { detail: unknown }) {
  const [showRaw, setShowRaw] = useState(false);

  if (detail == null || detail === "") return <span>—</span>;

  // Parse JSON strings if possible.
  let value = detail;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      // Not JSON, render as plain text.
      return <span className="text-brand-text">{String(value)}</span>;
    }
  }

  if (typeof value !== "object") {
    return <span className="text-brand-text">{String(value)}</span>;
  }

  const entries = Object.entries(value as Record<string, unknown>).filter(
    ([, v]) => v != null && v !== "",
  );

  if (entries.length === 0) return <span>—</span>;

  return (
    <div className="space-y-1">
      <dl className="space-y-0.5">
        {entries.map(([k, v]) => (
          <div key={k} className="flex flex-wrap gap-x-1.5">
            <dt className="text-brand-muted">{humanizeAuditKey(k)}:</dt>
            <dd className="break-all text-brand-text">{formatAuditValue(v)}</dd>
          </div>
        ))}
      </dl>

      <button
        type="button"
        onClick={() => setShowRaw((v) => !v)}
        className="text-xs text-brand-muted underline-offset-2 hover:text-brand-text hover:underline"
      >
        {showRaw ? "Hide raw" : "Show raw"}
      </button>

      {showRaw && (
        <pre className="mt-1 overflow-x-auto rounded bg-brand-bg p-2 text-xs text-brand-muted">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ----- Signature audit panel ------------------------------------------------

/** Coarse human-readable UA. Mirrors pdf_render.parse_user_agent on
 * the backend so the dashboard's "Browser" label matches what's
 * stamped into the PDF footer. Order matters: Edge contains
 * "Chrome/" so check Edg/ first; iOS UAs contain "like Mac OS X"
 * so check iPhone/iPad before Mac. */
function parseUserAgent(ua: string | null | undefined): string {
  if (!ua) return "unknown";
  let browser = "Other browser";
  if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Chrome/") || ua.includes("CriOS")) browser = "Chrome";
  else if (ua.includes("Firefox/") || ua.includes("FxiOS")) browser = "Firefox";
  else if (ua.includes("Safari/")) browser = "Safari";
  let os = "Other OS";
  if (ua.includes("iPhone") || ua.includes("iPad") || ua.includes("iPod"))
    os = "iOS";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("Mac OS X") || ua.includes("Macintosh")) os = "macOS";
  else if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Linux")) os = "Linux";
  return `${browser} on ${os}`;
}

interface SignatureAuditPanelProps {
  meta: MnspSignatureMeta | null;
  pdfSha256: string | null;
  onVerify: () => void;
  verifying: boolean;
  verifyResult: null | {
    kind: "match" | "mismatch" | "error";
    stored?: string;
    recomputed?: string;
    message?: string;
  };
}

/** Forensic signature audit panel. Renders the captured signing
 * IP / UA / timestamp + the rendered PDF's SHA-256 + a Verify button
 * that re-hashes the stashed S3 PDF and confirms tamper-evidence. */
function SignatureAuditPanel({
  meta,
  pdfSha256,
  onVerify,
  verifying,
  verifyResult,
}: SignatureAuditPanelProps) {
  const browser = parseUserAgent(meta?.userAgent);
  return (
    <div className="mt-4 rounded border border-brand-border bg-brand-bg p-3">
      <p className="mb-2 text-xs uppercase tracking-wide text-brand-muted">
        Electronic signature audit trail
      </p>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-brand-muted">Signed at</dt>
        <dd className="font-mono text-brand-text">
          {meta?.signedAt ? new Date(meta.signedAt).toLocaleString() : "—"}
        </dd>
        <dt className="text-brand-muted">Source IP</dt>
        <dd className="font-mono text-brand-text">{meta?.ip || "—"}</dd>
        <dt className="text-brand-muted">Browser</dt>
        <dd className="text-brand-text">{meta?.userAgent ? browser : "—"}</dd>
        {meta?.lastPatchIp && meta.lastPatchIp !== meta?.ip && (
          <>
            <dt className="text-brand-muted">Last edit IP</dt>
            <dd className="font-mono text-brand-text">{meta.lastPatchIp}</dd>
          </>
        )}
      </dl>

      {pdfSha256 && (
        <div className="mt-3 border-t border-brand-border pt-3">
          <p className="mb-1 text-xs uppercase tracking-wide text-brand-muted">
            Document hash (SHA-256)
          </p>
          <code className="block break-all text-xs text-brand-text">
            {pdfSha256}
          </code>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={onVerify}
              disabled={verifying}
              className="rounded border border-brand-border bg-brand-surface px-2 py-1 text-xs hover:bg-brand-bg disabled:cursor-not-allowed disabled:opacity-50"
            >
              {verifying ? "Verifying…" : "Verify against current PDF"}
            </button>
            {verifyResult?.kind === "match" && (
              <span className="text-xs text-success">
                ✓ Document unchanged since signing
              </span>
            )}
            {verifyResult?.kind === "mismatch" && (
              <span
                className="text-xs text-danger"
                title={`stored: ${verifyResult.stored}\nrecomputed: ${verifyResult.recomputed}`}
              >
                ⚠ MISMATCH — stored hash differs from current PDF
              </span>
            )}
            {verifyResult?.kind === "error" && (
              <span className="text-xs text-amber-400">
                {verifyResult.message}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
